/**
 * Bundle/product free gift RECONCILER (e.g. The Complexion Set Tyvek Bag).
 *
 * Port of the live theme's app/components/cart/cartModel.js free-gift logic.
 * Bundle ATC flows (product-bundle section, bundle-new stepper, kit builder)
 * stamp every line they add with a `_free_gift_variant` property (sourced
 * from the product's custom.free_gift_with_product metafield) AND include the
 * gift variant — its own line with the `_This is a free gift variant`
 * property, hidden by the cart drawer, visible at checkout — in the SAME
 * add call; the drawer's bundle REMOVE likewise drops the gift's share in the
 * same update call. So on the normal path this element finds a converged cart
 * and performs ZERO writes. It only steps in to repair drift: gift missing
 * (legacy carts, failed calls, app interference), wrong quantity, or orphan/
 * duplicate gift lines after the referencing bundle is gone. The checkout-side
 * complexion-set-validation extension removes orphan $0 gift lines using the
 * same `_free_gift_variant` reference, so the property names must not change.
 *
 * Quantity rules (same as live):
 * - bundle lines (`_bundle_id` present): one gift per full bundle — the min
 *   line quantity across the bundle's lines.
 * - standalone lines carrying `_free_gift_variant`: one gift per unit.
 *
 * Write discipline — this theme caches and synthesizes /cart.js responses
 * (global fetch patch in theme.liquid + component-cart-hydrate.js merging add
 * payloads into cached carts), and /cart/add.js MERGES quantities into an
 * existing identical line. A blind add based on a stale read therefore grows
 * the gift line instead of correcting it, and every write fires events other
 * cart components react to. So:
 * - all triggers coalesce into one debounced sync pass;
 * - a pass re-reads the cart FRESH (cache cleared, deduped GET) and only then
 *   decides; quantity fixes go through change-by-key (absolute, idempotent);
 *   add only runs when the fresh cart confirms the gift line is absent;
 * - writes are serialized, skipped while another cart request is in flight,
 *   and rate-limited — repeated corrective batches suspend the sync instead
 *   of ping-ponging with whatever keeps mutating the cart.
 */
const REFERENCE_PROPERTY = '_free_gift_variant';
const GIFT_PROPERTY = '_This is a free gift variant';
const INITIATOR = 'cart-free-gift';
const SYNC_DEBOUNCE_MS = 300;
const WRITE_COOLDOWN_MS = 1500;
const MAX_WRITE_STREAK = 3;
const SUSPEND_MS = 10000;

export class CartFreeGift extends HTMLElement {
  constructor() {
    super();
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onCartOpen = this.onCartOpen.bind(this);
    this.syncTimer = null;
    this.running = false;
    this.rerun = false;
    this.lastWriteAt = 0;
    this.writeStreak = 0;
    this.suspendedUntil = 0;
  }

  connectedCallback() {
    // Engine-driven cart changes (drawer steppers, remove buttons, other
    // components' writes). Our own requests are skipped via the initiator.
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartUpdate);
    // Bundle ATC flows post /cart/add.js directly (window.JR.addSetThenOpen)
    // and announce themselves with a window `cart-open` — no engine event fires.
    window.addEventListener('cart-open', this.onCartOpen);
    // A returning visitor can land with bundle lines already in the cart but
    // the gift missing or drifted.
    this.scheduleSync();
  }

  disconnectedCallback() {
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartUpdate);
    window.removeEventListener('cart-open', this.onCartOpen);
    clearTimeout(this.syncTimer);
  }

  onCartOpen() {
    this.scheduleSync();
  }

  onCartUpdate(event) {
    const { requestState } = event.detail || {};
    if (!requestState?.responseData) return;
    // Our own writes re-trigger request-end; the post-batch re-verify pass is
    // handled by `rerun`, not by reacting to ourselves.
    if (requestState.info?.initiator === INITIATOR) return;
    if (!requestState.responseData.ok) return;
    this.scheduleSync();
  }

  // Coalesce event bursts (ATC + cart-open + reactions) into one pass.
  scheduleSync() {
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => this.runSync(), SYNC_DEBOUNCE_MS);
  }

  async runSync() {
    if (this.running) {
      this.rerun = true;
      return;
    }
    if (!window.liquidAjaxCart) {
      this.whenEngineReady(() => this.scheduleSync());
      return;
    }
    // Never interleave with an in-flight cart request — its response would be
    // stale by the time our write lands (and line keys may change under us).
    if (window.liquidAjaxCart.processing) {
      this.scheduleSync();
      return;
    }

    this.running = true;
    try {
      // Phase 1: cheap cache-friendly read. For carts with no gift
      // involvement (the vast majority of shoppers) this is where it ends —
      // zero writes and no disturbance of the shared /cart.js cache.
      const probe = await this.fetchCart();
      if (!Array.isArray(probe?.items)) return;
      if (!this.computeOps(probe.items).length) {
        this.writeStreak = 0;
        return;
      }

      // Phase 2: something looks off — re-read FRESH (bypassing the cache
      // and cart-hydrate's client-merged carts) and recompute, so a write is
      // never based on stale or synthetic data.
      const cart = await this.fetchFreshCart();
      const items = cart?.items;
      if (!Array.isArray(items)) return;

      const ops = this.computeOps(items);
      if (!ops.length) {
        this.writeStreak = 0;
        return;
      }

      // Loop guard: converging carts need at most a couple of corrective
      // batches. A sustained stream means something else keeps fighting us
      // (an app, another component) — suspend instead of escalating.
      const now = Date.now();
      if (now < this.suspendedUntil) return;
      this.writeStreak = now - this.lastWriteAt < WRITE_COOLDOWN_MS + SYNC_DEBOUNCE_MS
        ? this.writeStreak + 1
        : 1;
      this.lastWriteAt = now;
      if (this.writeStreak > MAX_WRITE_STREAK) {
        this.suspendedUntil = now + SUSPEND_MS;
        console.warn('[cart-free-gift] repeated corrective writes — sync suspended');
        return;
      }

      await this.runOps(ops);
      // One re-verify pass after our own writes (their request-end is skipped).
      this.rerun = true;
    } catch (e) {
      /* aborted batch or network error — next external event re-syncs */
    } finally {
      this.running = false;
      if (this.rerun) {
        this.rerun = false;
        this.scheduleSync();
      }
    }
  }

  fetchCart() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  // The global fetch patch caches /cart.js for 3s and cart-hydrate re-seeds
  // that cache with client-merged carts. Decisions that lead to writes must
  // come from the server, so drop the cache before reading.
  fetchFreshCart() {
    if (window.__cartJsCache && typeof window.__cartJsCache.clear === 'function') {
      window.__cartJsCache.clear();
    }
    return this.fetchCart();
  }

  // liquidAjaxCart is published by native-ajax-cart; the initial page-load
  // check can run before that module has executed.
  whenEngineReady(fn, attempt = 0) {
    if (window.liquidAjaxCart) return fn();
    if (attempt > 20) return;
    setTimeout(() => this.whenEngineReady(fn, attempt + 1), 250);
  }

  // { giftVariantId: requiredQuantity } from all lines referencing a gift.
  computeRequiredGifts(items) {
    const bundleMinQuantities = {};
    const bundleGiftMap = {};
    const required = {};

    items.forEach((item) => {
      const props = item.properties || {};
      if (props[GIFT_PROPERTY]) return;
      const bundleId = props._bundle_id;
      const giftVariantId = props[REFERENCE_PROPERTY]?.toString();
      const quantity = Number(item.quantity) || 0;

      if (bundleId) {
        bundleMinQuantities[bundleId] =
          bundleId in bundleMinQuantities
            ? Math.min(bundleMinQuantities[bundleId], quantity)
            : quantity;
        if (giftVariantId) bundleGiftMap[bundleId] = giftVariantId;
      } else if (giftVariantId) {
        required[giftVariantId] = (required[giftVariantId] || 0) + quantity;
      }
    });

    Object.entries(bundleMinQuantities).forEach(([bundleId, minQty]) => {
      const giftVariantId = bundleGiftMap[bundleId];
      if (!giftVariantId) return;
      required[giftVariantId] = (required[giftVariantId] || 0) + minQty;
    });

    return required;
  }

  computeOps(items) {
    const required = this.computeRequiredGifts(items);
    const giftLines = items.filter((item) => item.properties?.[GIFT_PROPERTY]);
    const keptKeys = new Set();
    const ops = [];

    Object.entries(required).forEach(([variantId, quantity]) => {
      const existing = giftLines.find((line) => String(line.variant_id) === variantId);
      if (!existing) {
        ops.push({ type: 'add', variantId: Number(variantId), quantity });
        return;
      }
      keptKeys.add(existing.key);
      if (existing.quantity !== quantity) {
        // change-by-key SETS the quantity — safe against double-application,
        // unlike add.js which merges (existing + N).
        ops.push({ type: 'change', key: existing.key, quantity });
      }
    });

    // Unreferenced gift lines AND duplicate gift lines for an already-kept
    // variant (possible residue of interrupted syncs) are removed.
    giftLines.forEach((line) => {
      if (!keptKeys.has(line.key)) {
        ops.push({ type: 'change', key: line.key, quantity: 0 });
      }
    });

    return ops;
  }

  // One request at a time — parallel change.js calls race on line keys.
  // Rejects on the first failed op so a dead variant can't be hammered.
  runOps(ops) {
    return ops.reduce(
      (chain, op) => chain.then(() => this.runOp(op)),
      Promise.resolve()
    );
  }

  runOp(op) {
    return new Promise((resolve, reject) => {
      const options = {
        info: { initiator: INITIATOR },
        lastCallback: (requestState) => {
          if (requestState?.responseData?.ok) resolve();
          else reject(new Error('cart-free-gift op failed'));
        },
      };

      if (op.type === 'add') {
        const properties = { [GIFT_PROPERTY]: true };
        const settings = window.settings || {};
        if (
          settings.enable_test_promotion &&
          settings.test_promotion_property &&
          settings.test_promotion_property_value
        ) {
          properties[settings.test_promotion_property] = settings.test_promotion_property_value;
        }
        window.liquidAjaxCart.add(
          { items: [{ id: op.variantId, quantity: op.quantity, properties }] },
          options
        );
      } else {
        window.liquidAjaxCart.change({ id: op.key, quantity: op.quantity }, options);
      }
    });
  }
}

if (!customElements.get('cart-free-gift')) {
  customElements.define('cart-free-gift', CartFreeGift);
}
